import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { Avatar } from '@/components/Avatar';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { Topbar } from '@/components/Topbar';
import { useAsync } from '@/hooks/useAsync';
import { haptic } from '@/lib/telegram';
import type { AccountCheck } from '@/types/domain';

export function AccountDetailPage(): JSX.Element {
  const { id } = useParams();
  const accountId = Number(id);
  const { data: account, loading, error, reload } = useAsync(() => api.getAccount(accountId), [accountId]);
  const [checkResult, setCheckResult] = useState<AccountCheck | null>(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  const runCheck = async () => {
    try {
      setCheckLoading(true);
      setCheckError(null);
      haptic('medium');
      const result = await api.checkAccount(accountId);
      setCheckResult(result);
      reload();
    } catch (err) {
      setCheckError(String((err as Error)?.message || err));
    } finally {
      setCheckLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Topbar title="Аккаунт" />
        <div className="skeleton h-32" />
        <div className="skeleton h-24" />
        <div className="skeleton h-52" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="space-y-4">
        <Topbar title="Аккаунт" />
        <EmptyState
          icon="users"
          title="Аккаунт не найден"
          body={error || 'По этому id запись не вернулась из API.'}
        />
      </div>
    );
  }

  const display = checkResult?.account ?? account;
  const profile = checkResult?.telegram.me;
  const avatar = profile?.avatarDataUrl || undefined;

  return (
    <div className="space-y-4 pb-8">
      <Topbar title={display.name || display.username || `Аккаунт #${display.id}`} subtitle={`id ${display.id}`} />

      <section className="card-hero p-5">
        <div className="relative z-10 flex items-center gap-4">
          <Avatar
            name={display.name || display.username || `acc${display.id}`}
            src={avatar}
            size={88}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="truncate text-[18px] font-semibold text-white">
                {display.name || display.username || `Account ${display.id}`}
              </div>
              <StatusPill status={display.status} />
            </div>
            <div className="mt-2 space-y-1 text-[13px] text-white/70">
              <div>{display.username ? `@${display.username.replace(/^@/, '')}` : 'без username'}</div>
              <div>{display.phone || 'телефон не получен'}</div>
              <div>{display.proxy ? `${display.proxy.ip}:${display.proxy.port}` : 'proxy не назначен'}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <button
          onClick={runCheck}
          disabled={checkLoading}
          className="menu-tile flex min-h-[122px] flex-col justify-between p-4 text-left transition active:scale-[0.992] disabled:opacity-70"
        >
          <span className="icon-circle-ghost">
            <Icon name={checkLoading ? 'refresh' : 'shield'} size={20} className={checkLoading ? 'animate-spin' : ''} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-white">Проверить</div>
            <div className="mt-1 text-[12px] leading-5 text-[#8088ab]">
              getMe, proxy, flood wait, диалоги
            </div>
          </div>
        </button>

        <Link
          to={`/accounts/${display.id}/dialogs`}
          onClick={() => haptic('light')}
          className="menu-tile flex min-h-[122px] flex-col justify-between p-4 transition active:scale-[0.992]"
        >
          <span className="icon-circle-ghost">
            <Icon name="inbox" size={20} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-white">Диалоги</div>
            <div className="mt-1 text-[12px] leading-5 text-[#8088ab]">
              все активные диалоги
            </div>
          </div>
        </Link>

        <Link
          to={`/accounts/${display.id}/logs`}
          onClick={() => haptic('light')}
          className="menu-tile flex min-h-[122px] flex-col justify-between p-4 transition active:scale-[0.992]"
        >
          <span className="icon-circle-ghost">
            <Icon name="search" size={20} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-white">Логи</div>
            <div className="mt-1 text-[12px] leading-5 text-[#8088ab]">
              будущие и завершённые задачи
            </div>
          </div>
        </Link>
      </section>

      {checkError && (
        <section className="card p-4">
          <div className="text-[15px] font-semibold text-[#ffb4c6]">Проверка завершилась с ошибкой</div>
          <div className="mt-2 text-[13px] text-[#ffcfda]">{checkError}</div>
        </section>
      )}

      <section className="card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[15px] font-semibold text-white">Состояние Telegram</div>
            <div className="mt-1 text-[13px] text-[#7e87aa]">
              Актуальные данные появятся после `getMe`.
            </div>
          </div>
          {checkResult?.telegram.ok ? (
            <span className="rounded-full border border-[#285b45] bg-[rgba(33,91,61,0.22)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#59dc97]">
              ok
            </span>
          ) : checkResult ? (
            <span className="rounded-full border border-[#6d3349] bg-[rgba(109,51,73,0.22)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#ff9db8]">
              error
            </span>
          ) : null}
        </div>

        {profile ? (
          <div className="mt-4 grid gap-3 text-[13px] text-[#d9def5]">
            <InfoRow label="Телефон" value={profile.phone || display.phone || 'не вернулся'} />
            <InfoRow
              label="Имя / фамилия"
              value={[profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'не вернулось'}
            />
            <InfoRow label="Username" value={profile.username ? `@${profile.username}` : 'не задан'} />
            <InfoRow label="Telegram ID" value={profile.telegramId || 'не вернулся'} />
            <InfoRow
              label="Аватар"
              value={profile.avatarDataUrl ? 'получен и отображается выше' : 'не найден у аккаунта'}
            />
          </div>
        ) : (
          <div className="mt-4 text-[13px] text-[#7e87aa]">
            {checkResult?.telegram.error || 'Проверка ещё не запускалась.'}
          </div>
        )}
      </section>

      <section className="card p-4">
        <div className="text-[15px] font-semibold text-white">Сеть и ограничения</div>
        <div className="mt-4 grid gap-3 text-[13px] text-[#d9def5]">
          <InfoRow
            label="Proxy connection"
            value={
              !checkResult
                ? 'нет данных'
                : checkResult.proxy.connection.ok
                  ? 'ok'
                  : checkResult.proxy.connection.error || 'ошибка подключения'
            }
          />
          <InfoRow
            label="Внешний IP"
            value={checkResult?.proxy.externalIp || (display.proxy ? display.proxy.ip : 'нет proxy')}
          />
          <InfoRow
            label="Flood wait"
            value={checkResult ? `${checkResult.runtime.floodWaitSeconds} сек` : 'нет данных'}
          />
          <InfoRow
            label="Cooldown until"
            value={checkResult?.runtime.cooldownUntil ? formatDateTime(checkResult.runtime.cooldownUntil) : 'нет'}
          />
          <InfoRow
            label="Proxy error count"
            value={checkResult ? String(checkResult.runtime.proxyErrorCount) : 'нет данных'}
          />
        </div>
      </section>

      <section className="card p-4">
        <div className="text-[15px] font-semibold text-white">Диалоги</div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <MetricCard label="всего" value={String(checkResult?.dialogs.total ?? '—')} />
          <MetricCard label="непрочитано" value={String(checkResult?.dialogs.unread ?? '—')} />
        </div>
      </section>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 rounded-[18px] border border-white/6 bg-white/4 px-4 py-3">
      <div className="text-[#8b94b7]">{label}</div>
      <div className="max-w-[60%] text-right text-white">{value}</div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-[22px] border border-white/8 bg-white/4 px-4 py-4">
      <div className="text-[22px] font-semibold text-white">{value}</div>
      <div className="mt-1 text-[12px] uppercase tracking-[0.14em] text-[#7d85aa]">{label}</div>
    </div>
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
